/**
 * Client billing hooks: the BILLING_LIVE status (cached), the viewer's
 * entitlement, and the Stripe Checkout / Customer Portal launchers.
 *
 * `useBillingStatus` returns `null` until known. The last known value is kept
 * in memory + localStorage and mirrored to `<html data-billing>` so the
 * pre-paint bootstrap (`billingHintBootstrap` in `paywall.ts`) can render the
 * right Pro-lesson placeholder on the next page load without a flash.
 */
import { useAppAuth } from './auth/app-auth'
import { ConvexError } from 'convex/values'
import { useAction, useConvexAuth, useQuery } from 'convex/react'
import { useCallback, useEffect, useState } from 'react'
import { api } from '../../convex/_generated/api'
import { trackEvent } from './analytics'
import type { ConversionSurface } from './analytics'
import type { CheckoutIntent } from './billing-intent'
import type { Locale } from './i18n/config'
import { BILLING_HINT_DATASET_KEY, BILLING_HINT_STORAGE_KEY } from './paywall'
import type { PlanId } from './pricing'

let lastKnownLive: boolean | null = null

function persistBillingHint(live: boolean) {
  lastKnownLive = live
  try {
    document.documentElement.dataset[BILLING_HINT_DATASET_KEY] = live
      ? 'live'
      : 'early'
    window.localStorage.setItem(BILLING_HINT_STORAGE_KEY, live ? 'live' : 'early')
  } catch {
    // Private mode / blocked storage: the hint is an optimisation only.
  }
}

/** `{ live }` from `billing.status`; `null` while unknown. */
export function useBillingStatus(): boolean | null {
  const status = useQuery(api.billing.status, {})
  const live = status?.live ?? null
  // After hydration (never during it, to keep SSR markup identical), fall
  // back to this browser's last known mode until the query answers.
  const [cached, setCached] = useState<boolean | null>(lastKnownLive)
  useEffect(() => {
    if (live !== null) {
      persistBillingHint(live)
      return
    }
    try {
      const hint = window.localStorage.getItem(BILLING_HINT_STORAGE_KEY)
      if (hint === 'live' || hint === 'early') setCached(hint === 'live')
    } catch {
      // Storage blocked: stay unknown until the query answers.
    }
  }, [live])
  return live ?? cached
}

export type ViewerEntitlement = {
  signedIn: boolean
  /** True once Clerk + Convex agree on the auth state. */
  ready: boolean
  isPro: boolean
  plan: PlanId
  source: 'none' | 'admin' | 'subscription' | 'grant' | 'team_seat'
}

/** The signed-in viewer's entitlement (optimistic: signed out until known). */
export function useViewerEntitlement(): ViewerEntitlement {
  const { isLoaded, isSignedIn } = useAppAuth()
  const { isAuthenticated, isLoading } = useConvexAuth()
  const entitlement = useQuery(
    api.billing.myEntitlement,
    isAuthenticated ? {} : 'skip',
  )
  if (!isLoaded || !isSignedIn) {
    return {
      signedIn: false,
      ready: isLoaded,
      isPro: false,
      plan: 'free',
      source: 'none',
    }
  }
  if (isLoading || !isAuthenticated || entitlement === undefined) {
    return {
      signedIn: true,
      ready: false,
      isPro: false,
      plan: 'free',
      source: 'none',
    }
  }
  return {
    signedIn: true,
    ready: true,
    isPro: entitlement.isPro,
    plan: entitlement.plan,
    source: entitlement.source,
  }
}

export type BillingErrorCode =
  | 'AUTH_REQUIRED'
  | 'BILLING_NOT_LIVE'
  | 'BILLING_NOT_CONFIGURED'
  | 'RATE_LIMITED'
  | 'ALREADY_SUBSCRIBED'
  | 'NO_CUSTOMER'
  | 'CHECKOUT_FAILED'
  | 'PORTAL_FAILED'
  | 'UNKNOWN'

const KNOWN_CODES = new Set<BillingErrorCode>([
  'AUTH_REQUIRED',
  'BILLING_NOT_LIVE',
  'BILLING_NOT_CONFIGURED',
  'RATE_LIMITED',
  'ALREADY_SUBSCRIBED',
  'NO_CUSTOMER',
  'CHECKOUT_FAILED',
  'PORTAL_FAILED',
])

/** Extract the `{ code }` of a ConvexError (anything else is UNKNOWN). */
export function billingErrorCode(error: unknown): BillingErrorCode {
  if (error instanceof ConvexError) {
    const code = (error.data as { code?: unknown } | null)?.code
    if (typeof code === 'string' && KNOWN_CODES.has(code as BillingErrorCode)) {
      return code as BillingErrorCode
    }
  }
  return 'UNKNOWN'
}

/** Checkout + portal launchers with pending/error state. */
export function useBillingActions(locale: Locale) {
  const createCheckout = useAction(api.stripeActions.createCheckoutSession)
  const createPortal = useAction(api.stripeActions.createPortalSession)
  const [pending, setPending] = useState<'checkout' | 'portal' | null>(null)
  const [error, setError] = useState<BillingErrorCode | null>(null)

  const openPortal = useCallback(async () => {
    setPending('portal')
    setError(null)
    try {
      const { url } = await createPortal({ locale })
      window.location.assign(url)
    } catch (caught) {
      const code = billingErrorCode(caught)
      trackEvent('billing_action_failed', { action: 'portal', error_code: code })
      setError(code)
      setPending(null)
    }
  }, [createPortal, locale])

  const startCheckout = useCallback(
    async (intent: CheckoutIntent, surface: ConversionSurface) => {
      setPending('checkout')
      setError(null)
      trackEvent('checkout_started', {
        surface,
        plan: intent.plan,
        interval: intent.interval,
        seats: intent.plan === 'team' ? (intent.seats ?? 5) : 1,
      })
      try {
        const { url } = await createCheckout({
          plan: intent.plan,
          interval: intent.interval,
          seats: intent.plan === 'team' ? intent.seats : undefined,
          locale,
        })
        window.location.assign(url)
      } catch (caught) {
        const code = billingErrorCode(caught)
        if (code === 'ALREADY_SUBSCRIBED') {
          // Plan changes happen in the portal.
          await openPortal()
          return
        }
        trackEvent('billing_action_failed', {
          action: 'checkout',
          error_code: code,
          surface,
          plan: intent.plan,
          interval: intent.interval,
        })
        setError(code)
        setPending(null)
      }
    },
    [createCheckout, locale, openPortal],
  )

  return {
    startCheckout,
    openPortal,
    pending,
    error,
    clearError: () => setError(null),
  }
}
